import { isTodayWithinRange } from "../../lib/dateHelpers.js";
import {
  getCitation,
  getFormatted,
  mergeItems
} from "../../lib/formatUtilities.js";

export default {
  Title: "UNC CV DO NOT MAP",
  template: "/templateFiles/unc_template_v1_0.docx",
  outputSettings: { font: "Arial", fontSize: 10 },
  output: {
    bio: {
      field: "bio",
      map: {
        bio_name: "name",
        bio_officeAddress: "officeAddress",
        bio_workPhone: "workPhone",
        bio_workEmail: "workEmail",
        bio_workFax: "workFax"
      }
    },
    // Education
    edu: {
      field: "education",
      preface: true,
      format: {
        t: ["limitedMonthAndFullYear"],
        1: ["location", "secondaryInfo", "degree"]
      }
    },
    // Certification
    cert: {
      field: "clinicalActivityAndInnovations",
      type: "certification",
      preface: true,
      format: {
        t: ["limitedMonthAndFullYear"],
        1: ["title"]
      }
    },
    // Professional Experience
    pe: {
      field: "hospitalAppointments",
      preface: true,
      format: {
        t: ["limitedMonthAndFullYear"],
        1: [mergeItems([{ key: "description" }, { key: "title" }])]
      }
    },
    // Honors
    hon: {
      field: "honors",
      preface: true,
      format: {
        t: ["limitedMonthAndFullYear"],
        1: [mergeItems([{ key: "title" }, { key: "location" }])]
      }
    },
    // Bibliography
    bib: {
      field: "scholarship",
      preface: true,
      format: {
        1: function (item, i, cvDef, genOpts) {
          return i + 1;
        },
        2: function (item, i, cvDef, genOpts) {
          return [
            getCitation("line")(item, i, cvDef, genOpts),
            getFormatted("comments", { indent: 600 })(item, i, cvDef, genOpts)
          ].join(genOpts.noXML ? "<br>" : "");
        }
      },
      sections: [
        { title: "Books / Book Chapters", type: "booksAndChapters" },
        { title: "Book Chapters - In Press / Submitted", type: "chapters" },
        { title: "Refereed Articles", type: "reviewArticle" },
        {
          title: "Refereed Articles - In Press / Submitted",
          type: "Reviews, chapters, monographs and editorials"
        },
        { title: "Refereed Other Products of Scholarship", type: "reviews" },
        {
          title: "Products of Interdisciplinary Scholarship",
          type: "interdisciplinary"
        },
        { title: "Products of Engaged Scholarship", type: "engaged" },
        { title: "Products of Creative Activity", type: "creativeWriting" },
        {
          title: "Digital and Other Novel Forms of Scholarship",
          type: "media"
        },
        {
          title: "Refereed Unpublished Oral Presentations and/or Abstracts",
          type: "abstracts"
        },
        { title: "Other", type: "otherPeer" }
      ]
    },
    // Teaching Activities
    teach: {
      field: "teaching",
      preface: true,
      group: {
        tcCourse: {
          type: "Clinical Supervisory and Training Responsibilities",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcLect: {
          type: "Teaching of Students in Courses",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcGrand: {
          type: "counseling",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcCont: {
          type: "Formal Teaching of Peers (e.g., CME and other continuing education courses)",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcTrain: {
          type: "educationalLeadership",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcLab: {
          type: "Laboratory and Other Research Supervisory and Training",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [mergeItems([{ key: "title", bold: true }]), "description"],
            2: ["audience"],
            3: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            4: ["number"]
          }
        },
        tcClinTeach: {
          type: "Formal Teaching of Residents, Clinical Fellows and Research Fellows (post-docs)",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: ["institution"],
            2: [mergeItems([{ prefix: "Role: ", key: "role" }])],
            3: ["title"],
            4: ["audience"],
            5: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            6: ["number"]
          }
        },
        tcStud: {
          type: "Other Mentored Trainees and Faculty",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: ["title"],
            2: ["description"],
            3: ["audience"],
            4: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            5: ["number"]
          }
        },
        tcClinServ: {
          type: "clinicalService",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: ["title"],
            2: ["description"],
            3: ["audience"],
            4: [mergeItems([{ prefix: "Duration: ", key: "sessionLength" }])],
            5: ["number"]
          }
        },
        tcGrad: {
          type: "postdocs",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: ["title", "description"]
          }
        },
        tcOth: {
          type: "external",
          format: {
            t: ["limitedMonthAndFullYear"],
            1: [
              mergeItems([
                { key: "title", bold: true },
                { prefix: "Mentored: ", key: "project", italics: true }
              ]),
              mergeItems([{ prefix: "Project: ", key: "description" }]),
              mergeItems([{ prefix: "Presented: ", key: "location" }])
            ]
          }
        }
      }
    },
    // Grants
    grant: {
      field: "projects",
      preface: true,
      format: {
        1: ["grantTypeAndNumber"],
        2: ["piName"],
        3: ["limitedMonthAndFullYear", "directCosts"],
        4: ["workTime"],
        5: [
          "title",
          "description",
          mergeItems([{ prefix: "Role: ", key: "role" }])
        ]
      },
      sections: [
        {
          title: "ACTIVE",
          filter: function (item) {
            return item.type == "funded" && isTodayWithinRange(item.time);
          }
        },
        { title: "PENDING", type: "submitted" },
        {
          title: "COMPLETED",
          filter: function (item) {
            return item.type == "funded" && !isTodayWithinRange(item.time);
          }
        }
      ]
    },
    // Professional Service
    ps: {
      field: "committeeService",
      preface: true,
      format: {
        t: ["limitedMonthAndFullYear"],
        1: ["role"]
      },
      sections: [
        { title: "Professional Service to Discipline", type: "dept" },
        { title: "Professional Service within UNC", type: "institutional" },
        { title: "Other", type: "other" }
      ]
    },
    // Statements
    statements: {
      field: "researchInterests",
      map: {
        st_researchStatement: "description",
        st_teachingStatement: "mentoring",
        st_deiStatement: "deei",
        st_serviceStatement: "clinical"
      }
    }
  }
};
